import type { AssetNotification } from '@/services/notificationsService';

/**
 * Where a notification should take the person clicking it.
 *
 * Shared by the Notifications page and the Header dropdown so both open the
 * same record the same way. The URL is built here from the record the
 * notification is about, not taken from the stored `link` verbatim: the same
 * incident or ticket lives on a different page for a platform admin than for
 * an org user, and older rows carry links to routes that no longer exist
 * (/nirp/incidents/:id, /support/tickets/:id), which fell through to the
 * landing page and looked like a logout.
 */
export type NotificationTarget =
  | { kind: 'link'; to: string }
  /** There is a record, but this user may no longer open it. */
  | { kind: 'denied' }
  | null;

export interface NotificationViewer {
  isPlatformAdmin: boolean;
  hasPermission: (permission: string) => boolean;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function match(link: string | null, pattern: RegExp): string | null {
  if (!link) return null;
  const m = link.match(pattern);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function gated(permission: string, to: string, viewer: NotificationViewer): NotificationTarget {
  return viewer.hasPermission(permission) ? { kind: 'link', to } : { kind: 'denied' };
}

export function resolveNotificationTarget(
  n: Pick<AssetNotification, 'link' | 'metadata'>,
  viewer: NotificationViewer,
): NotificationTarget {
  const meta = n.metadata ?? {};
  const link = str(n.link);

  const incidentId =
    str(meta.incidentId) ??
    match(link, /^\/nirp\/(?:admin\/)?incidents\/([^/?#]+)/) ??
    match(link, /^\/nirp\/(?:admin\/)?incidents\?(?:.*&)?id=([^&#]+)/);
  if (incidentId) {
    const base = viewer.isPlatformAdmin ? '/nirp/admin/incidents' : '/nirp/incidents';
    return gated('incidents:read', `${base}?id=${encodeURIComponent(incidentId)}`, viewer);
  }

  const ticketId =
    str(meta.ticketId) ??
    match(link, /^\/support\/tickets\/([^/?#]+)/) ??
    match(link, /^\/(?:admin\/)?support\?(?:.*&)?ticket=([^&#]+)/);
  if (ticketId) {
    // Support is open to every signed-in user; staff answer from the admin page.
    const base = viewer.isPlatformAdmin ? '/admin/support' : '/support';
    return { kind: 'link', to: `${base}?ticket=${encodeURIComponent(ticketId)}` };
  }

  const caseId = str(meta.caseId) ?? match(link, /^\/cms\/cases\/([^/?#]+)/);
  if (caseId && caseId !== 'board') {
    return gated('cases:read', `/cms/cases/${encodeURIComponent(caseId)}`, viewer);
  }

  if (link?.startsWith('/cms/alerts')) {
    return gated('alerts:read', link, viewer);
  }

  const assetId = str(meta.assetId) ?? match(link, /^\/assets\/([^/?#]+)/);
  if (assetId && assetId !== 'dashboard' && assetId !== 'create') {
    const tab = match(link, /[?&]tab=([^&#]+)/);
    const to = `/assets/${encodeURIComponent(assetId)}${tab ? `?tab=${encodeURIComponent(tab)}` : ''}`;
    return gated('assets:read', to, viewer);
  }

  if (link?.startsWith('/vulnerabilities')) {
    return gated('vulnerabilities:read', link, viewer);
  }

  // The old "organization approved" link; /dashboard only redirects now.
  if (link === '/dashboard') return { kind: 'link', to: '/admin/dashboard' };

  if (link?.startsWith('/')) return { kind: 'link', to: link };

  return null;
}
